import React, { useState, useEffect, useCallback } from 'react';
import * as XLSX from 'xlsx';
import {
  getOrganizations,
  createOrganization,
  updateOrganization,
  deleteOrganization,
  getContacts,
  createContact,
  updateContact,
  deleteContact,
  importOrganizations,
  importContacts,
  getWebParticipantsImportStats,
  importCredencialesWeb,
  importNetworkingWeb,
  importInscripcionesGenerales,
  type WebParticipantsImportStats,
} from '../services/database';
import { RefreshIcon, XIcon } from './icons/AdminIcons';
import Modal from './Modal';
import './DatabaseSection.css';

interface DatabaseDataSectionProps {
  isDarkMode?: boolean;
  error: string | null;
  setError: (s: string | null) => void;
  successMessage: string | null;
  setSuccessMessage: (s: string | null) => void;
}

const DatabaseDataSection: React.FC<DatabaseDataSectionProps> = ({
  isDarkMode,
  error,
  setError,
  successMessage,
  setSuccessMessage,
}) => {
  const [organizations, setOrganizations] = useState<any[]>([]);
  const [contacts, setContacts] = useState<any[]>([]);
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importingWeb, setImportingWeb] = useState(false);
  const [webStats, setWebStats] = useState<WebParticipantsImportStats | null>(null);
  const [modal, setModal] = useState<'org' | 'contact' | null>(null);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formOrg, setFormOrg] = useState({ domain: '', name: '', description: '' });
  const [formContact, setFormContact] = useState({ email: '', domain: '', name: '', organization_id: '' as string | number });
  const PAGE_SIZE = 50;
  const [pageOrg, setPageOrg] = useState(1);
  const [pageContact, setPageContact] = useState(1);

  const loadOrganizations = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getOrganizations();
      setOrganizations(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar organizaciones');
    } finally {
      setLoading(false);
    }
  }, [setError]);

  const loadContacts = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getContacts(selectedOrgId ?? undefined);
      setContacts(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al cargar contactos');
    } finally {
      setLoading(false);
    }
  }, [selectedOrgId, setError]);

  const loadWebStats = useCallback(async () => {
    try {
      const stats = await getWebParticipantsImportStats();
      setWebStats(stats);
    } catch {
      setWebStats(null);
    }
  }, []);

  useEffect(() => { loadOrganizations(); }, [loadOrganizations]);
  useEffect(() => { loadContacts(); }, [loadContacts]);
  useEffect(() => { void loadWebStats(); }, [loadWebStats]);
  useEffect(() => { setPageOrg(1); }, [organizations.length]);
  useEffect(() => { setPageContact(1); }, [contacts.length, selectedOrgId]);

  const handleSaveOrg = async () => {
    try {
      const domainVal = formOrg.domain.trim() || undefined;
      if (editingId) {
        await updateOrganization(editingId, { domain: domainVal, name: formOrg.name || undefined, description: formOrg.description || undefined });
      } else {
        await createOrganization({ domain: domainVal, name: formOrg.name || undefined, description: formOrg.description || undefined });
      }
      setModal(null);
      setEditingId(null);
      setFormOrg({ domain: '', name: '', description: '' });
      loadOrganizations();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al guardar');
    }
  };

  const handleSaveContact = async () => {
    if (!formContact.email.trim()) { setError('Email es obligatorio'); return; }
    try {
      if (editingId) {
        await updateContact(editingId, {
          email: formContact.email.trim(),
          domain: formContact.domain || undefined,
          name: formContact.name || undefined,
          organization_id: formContact.organization_id ? parseInt(String(formContact.organization_id), 10) : undefined,
        });
      } else {
        await createContact({
          email: formContact.email.trim(),
          domain: formContact.domain || undefined,
          name: formContact.name || undefined,
          organization_id: formContact.organization_id ? parseInt(String(formContact.organization_id), 10) : undefined,
        });
      }
      setModal(null);
      setEditingId(null);
      setFormContact({ email: '', domain: '', name: '', organization_id: '' });
      loadContacts();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al guardar');
    }
  };

  const parseExcel = (file: File): Promise<any[]> => {
    return new Promise((resolve, reject) => {
      const r = new FileReader();
      r.onload = (e) => {
        try {
          const data = e.target?.result;
          const wb = XLSX.read(data, { type: 'array' });
          const first = wb.SheetNames[0];
          const sheet = wb.Sheets[first];
          const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });
          resolve(Array.isArray(rows) ? rows : []);
        } catch (err) {
          reject(err);
        }
      };
      r.onerror = () => reject(new Error('Error al leer el archivo'));
      r.readAsArrayBuffer(file);
    });
  };

  const CHUNK_SIZE = 400;

  const handleImportOrgs = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setImporting(true);
    setError(null);
    try {
      const rows = await parseExcel(file) as Record<string, unknown>[];
      if (!rows.length || !rows[0]) {
        setError('El Excel no tiene filas.');
        return;
      }
      const columns = Object.keys(rows[0]).filter((k) => k != null && String(k).trim() !== '');
      if (!columns.length) {
        setError('No se encontraron columnas en la primera fila.');
        return;
      }
      let totalInserted = 0, totalUpdated = 0;
      for (let i = 0; i < rows.length; i += CHUNK_SIZE) {
        const chunk = rows.slice(i, i + CHUNK_SIZE);
        const result = await importOrganizations({ columns, rows: chunk });
        totalInserted += (result as any).inserted ?? 0;
        totalUpdated += (result as any).updated ?? 0;
      }
      setSuccessMessage(`Organizaciones: ${totalInserted} insertadas, ${totalUpdated} actualizadas. Las columnas del Excel se agregaron a la tabla.`);
      loadOrganizations();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al importar');
    } finally {
      setImporting(false);
    }
  };

  const importWebExcel = async (
    file: File,
    importer: (payload: { columns: string[]; rows: Record<string, unknown>[]; sourceFile: string }) => Promise<{
      inserted?: number;
      updated?: number;
      skipped?: number;
    }>,
    label: string
  ) => {
    setImportingWeb(true);
    setError(null);
    try {
      const rows = (await parseExcel(file)) as Record<string, unknown>[];
      if (!rows.length || !rows[0]) {
        setError('El Excel no tiene filas.');
        return;
      }
      const columns = Object.keys(rows[0]).filter((k) => k != null && String(k).trim() !== '');
      if (!columns.length) {
        setError('No se encontraron columnas en la primera fila.');
        return;
      }
      let totalInserted = 0;
      let totalUpdated = 0;
      let totalSkipped = 0;
      for (let i = 0; i < rows.length; i += CHUNK_SIZE) {
        const chunk = rows.slice(i, i + CHUNK_SIZE);
        const result = await importer({ columns, rows: chunk, sourceFile: file.name });
        totalInserted += result.inserted ?? 0;
        totalUpdated += result.updated ?? 0;
        totalSkipped += result.skipped ?? 0;
      }
      await loadWebStats();
      setSuccessMessage(
        `${label}: ${totalInserted} insertados, ${totalUpdated} actualizados, ${totalSkipped} omitidos (faltan nombre, apellido, organización o correo válido). Aparecen en el modal Ver participantes.`
      );
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al importar');
    } finally {
      setImportingWeb(false);
    }
  };

  const handleImportCredencialesWeb = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    await importWebExcel(file, importCredencialesWeb, 'Credenciales web');
  };

  const handleImportNetworkingWeb = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    await importWebExcel(file, importNetworkingWeb, 'Networking web');
  };

  const handleImportInscripcionesGenerales = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    await importWebExcel(file, importInscripcionesGenerales, 'Inscripciones generales');
  };

  const handleImportContacts = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    setImporting(true);
    setError(null);
    try {
      const rows = await parseExcel(file) as Record<string, unknown>[];
      if (!rows.length || !rows[0]) {
        setError('El Excel no tiene filas.');
        return;
      }
      const columns = Object.keys(rows[0]).filter((k) => k != null && String(k).trim() !== '');
      if (!columns.length) {
        setError('No se encontraron columnas en la primera fila.');
        return;
      }
      let totalInserted = 0, totalUpdated = 0, totalSkipped = 0;
      for (let i = 0; i < rows.length; i += CHUNK_SIZE) {
        const chunk = rows.slice(i, i + CHUNK_SIZE);
        const result = await importContacts({ columns, rows: chunk });
        totalInserted += (result as any).inserted ?? 0;
        totalUpdated += (result as any).updated ?? 0;
        totalSkipped += (result as any).skipped ?? 0;
      }
      setSuccessMessage(`Contactos: ${totalInserted} insertados, ${totalUpdated} actualizados, ${totalSkipped} omitidos. Las columnas del Excel se agregaron a la tabla.`);
      loadContacts();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al importar');
    } finally {
      setImporting(false);
    }
  };

  return (
    <div className="database-data-section">
      <div className="admin-crm-section-card database-import-section">
        <h3>Participantes web (modal de networking)</h3>
        <p>
          Tres tablas para el listado <strong>Ver participantes</strong> en reservas de networking:{' '}
          <strong>Credenciales web</strong>, <strong>Networking web</strong> e <strong>Inscripciones generales</strong>.
          Subí un Excel por tabla. Si el correo ya existe en la misma tabla, se actualiza la fila.
        </p>
        {webStats ? (
          <p className="net-muted net-muted--tight" style={{ marginTop: 0 }}>
            En base: {webStats.credenciales_web} Credenciales web · {webStats.networking_web} Networking web ·{' '}
            {webStats.inscripciones_generales} Inscripciones generales · {webStats.total} en total (antes de deduplicar
            con el resto del modal).
          </p>
        ) : null}
        <div className="database-toolbar">
          <label className="database-import-label">
            Excel Credenciales web (.xlsx / .xls)
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImportCredencialesWeb}
              disabled={importingWeb || importing}
            />
          </label>
          <label className="database-import-label">
            Excel Networking web (.xlsx / .xls)
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImportNetworkingWeb}
              disabled={importingWeb || importing}
            />
          </label>
          <label className="database-import-label">
            Excel Inscripciones generales (.xlsx / .xls)
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImportInscripcionesGenerales}
              disabled={importingWeb || importing}
            />
          </label>
          <button
            type="button"
            className="database-btn database-btn-icon"
            onClick={() => void loadWebStats()}
            disabled={importingWeb}
            title="Actualizar conteos"
          >
            <RefreshIcon width={18} height={18} />
          </button>
          {importingWeb && <span>Importando participantes web…</span>}
        </div>
        <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--text-secondary, #64748b)' }}>
          Columnas reconocidas: <strong>nombre</strong> + <strong>apellido</strong> (o <strong>nombre completo</strong>
          ), <strong>organización</strong>, <strong>correo</strong> (obligatorio con @), opcional <strong>teléfono</strong>
          , <strong>cargo</strong>, <strong>tipo de organización</strong>.
        </p>
      </div>

      <div className="admin-crm-section-card database-import-section">
        <h3>Importar desde Excel a las tablas Organizaciones y Contactos</h3>
        <p>Hay <strong>dos tablas</strong> vinculadas: <strong>Organizaciones</strong> y <strong>Contactos</strong>. Subí primero el Excel de organizaciones y después el de contactos. En el Excel de contactos podés tener una columna con el <strong>nombre de la organización</strong> para vincular cada contacto; si no tiene o está vacía, el contacto se sube igual y queda sin vincular.</p>
        <div className="database-toolbar">
          <label className="database-import-label">
            Excel de Organizaciones (.xlsx / .xls)
            <input type="file" accept=".xlsx,.xls" onChange={handleImportOrgs} disabled={importing} />
          </label>
          <label className="database-import-label">
            Excel de Contactos (.xlsx / .xls)
            <input type="file" accept=".xlsx,.xls" onChange={handleImportContacts} disabled={importing} />
          </label>
          {importing && <span>Importando...</span>}
        </div>
        <p style={{ marginTop: '0.5rem', fontSize: '0.85rem', color: 'var(--text-secondary, #64748b)' }}>
          Las tablas quedan con <strong>las mismas columnas que cada Excel</strong>. Organizaciones: una columna de identificador (ej. <strong>Dominio</strong>), opcional. Contactos: columna <strong>Email</strong> obligatoria; columna con nombre de organización (ej. <strong>Organización</strong>) opcional — si no está o está vacía, el contacto queda en la tabla sin vincular.
        </p>
      </div>

      <div className="admin-crm-section-card">
      <h3>Organizaciones</h3>
      <div className="database-toolbar">
        <button type="button" className="database-btn database-btn-primary" onClick={() => { setEditingId(null); setFormOrg({ domain: '', name: '', description: '' }); setModal('org'); }}>
          Nueva organización
        </button>
        <button type="button" className="database-btn database-btn-icon" onClick={loadOrganizations}><RefreshIcon width={18} height={18} /></button>
      </div>
      {loading && !organizations.length ? <p>Cargando...</p> : (
        <>
        <div className="admin-users-table-container">
          <table className="database-table">
            <thead>
              <tr>
                <th>Id</th>
                {(organizations[0] ? Object.keys(organizations[0]).filter((k) => !['id', 'created_at', 'updated_at'].includes(k)) : ['domain', 'name', 'description']).map((k) => (
                  <th key={k}>{k}</th>
                ))}
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {organizations.slice((pageOrg - 1) * PAGE_SIZE, pageOrg * PAGE_SIZE).map((row) => {
                const cols = Object.keys(row).filter((k) => !['id', 'created_at', 'updated_at'].includes(k));
                return (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    {cols.map((k) => (
                      <td key={k}>{row[k] != null ? String(row[k]) : '-'}</td>
                    ))}
                    <td>
                      <button type="button" className="database-btn-link" onClick={() => { setEditingId(row.id); setFormOrg({ domain: row.domain ?? '', name: row.name ?? '', description: row.description ?? '' }); setModal('org'); }}>Editar</button>
                      <button type="button" className="database-btn-link database-btn-danger" onClick={async () => { if (window.confirm('¿Eliminar organización?')) { await deleteOrganization(row.id); loadOrganizations(); loadContacts(); } }}>Eliminar</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="database-pagination">
          <span>Mostrando {organizations.length ? (pageOrg - 1) * PAGE_SIZE + 1 : 0} - {Math.min(pageOrg * PAGE_SIZE, organizations.length)} de {organizations.length}</span>
          <div className="database-pagination-btns">
            <button type="button" className="database-btn" disabled={pageOrg <= 1} onClick={() => setPageOrg((p) => p - 1)}>Anterior</button>
            <span>Página {pageOrg} de {Math.max(1, Math.ceil(organizations.length / PAGE_SIZE))}</span>
            <button type="button" className="database-btn" disabled={pageOrg >= Math.ceil(organizations.length / PAGE_SIZE)} onClick={() => setPageOrg((p) => p + 1)}>Siguiente</button>
          </div>
        </div>
        </>
      )}
      </div>

      <div className="admin-crm-section-card">
      <h3>Contactos</h3>
      <div className="database-toolbar">
        <label>
          Organización:
          <select value={selectedOrgId ?? ''} onChange={(e) => setSelectedOrgId(e.target.value ? parseInt(e.target.value, 10) : null)}>
            <option value="">Todas</option>
            {organizations.map((o) => <option key={o.id} value={o.id}>{o.name || o.domain || o.id}</option>)}
          </select>
        </label>
        <button type="button" className="database-btn database-btn-primary" onClick={() => { setEditingId(null); setFormContact({ email: '', domain: '', name: '', organization_id: selectedOrgId ?? '' }); setModal('contact'); }}>
          Nuevo contacto
        </button>
        <button type="button" className="database-btn database-btn-icon" onClick={loadContacts}><RefreshIcon width={18} height={18} /></button>
      </div>
      {loading && !contacts.length ? <p>Cargando...</p> : (
        <>
        <div className="admin-users-table-container">
          <table className="database-table">
            <thead>
              <tr>
                <th>Id</th>
                {(contacts[0] ? Object.keys(contacts[0]).filter((k) => !['id', 'created_at', 'updated_at'].includes(k)) : ['email', 'domain', 'name', 'organization_name']).map((k) => (
                  <th key={k}>{k}</th>
                ))}
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {contacts.slice((pageContact - 1) * PAGE_SIZE, pageContact * PAGE_SIZE).map((row) => {
                const cols = Object.keys(row).filter((k) => !['id', 'created_at', 'updated_at'].includes(k));
                return (
                  <tr key={row.id}>
                    <td>{row.id}</td>
                    {cols.map((k) => (
                      <td key={k}>{row[k] != null ? String(row[k]) : '-'}</td>
                    ))}
                    <td>
                      <button type="button" className="database-btn-link" onClick={() => { setEditingId(row.id); setFormContact({ email: row.email, domain: row.domain ?? '', name: row.name ?? '', organization_id: row.organization_id ?? '' }); setModal('contact'); }}>Editar</button>
                      <button type="button" className="database-btn-link database-btn-danger" onClick={async () => { if (window.confirm('¿Eliminar contacto?')) { await deleteContact(row.id); loadContacts(); } }}>Eliminar</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div className="database-pagination">
          <span>Mostrando {contacts.length ? (pageContact - 1) * PAGE_SIZE + 1 : 0} - {Math.min(pageContact * PAGE_SIZE, contacts.length)} de {contacts.length}</span>
          <div className="database-pagination-btns">
            <button type="button" className="database-btn" disabled={pageContact <= 1} onClick={() => setPageContact((p) => p - 1)}>Anterior</button>
            <span>Página {pageContact} de {Math.max(1, Math.ceil(contacts.length / PAGE_SIZE))}</span>
            <button type="button" className="database-btn" disabled={pageContact >= Math.ceil(contacts.length / PAGE_SIZE)} onClick={() => setPageContact((p) => p + 1)}>Siguiente</button>
          </div>
        </div>
        </>
      )}
      </div>

      {modal === 'org' && (
        <Modal isOpen onClose={() => setModal(null)} title={editingId ? 'Editar organización' : 'Nueva organización'}>
          <div className="database-form">
            <label>Dominio (opcional) <input value={formOrg.domain} onChange={(e) => setFormOrg((f) => ({ ...f, domain: e.target.value }))} placeholder="ej. empresa.com" /></label>
            <label>Nombre <input value={formOrg.name} onChange={(e) => setFormOrg((f) => ({ ...f, name: e.target.value }))} /></label>
            <label>Descripción <input value={formOrg.description} onChange={(e) => setFormOrg((f) => ({ ...f, description: e.target.value }))} /></label>
            <div className="database-form-actions">
              <button type="button" className="database-btn" onClick={() => setModal(null)}>Cancelar</button>
              <button type="button" className="database-btn database-btn-primary" onClick={handleSaveOrg}>Guardar</button>
            </div>
          </div>
        </Modal>
      )}

      {modal === 'contact' && (
        <Modal isOpen onClose={() => setModal(null)} title={editingId ? 'Editar contacto' : 'Nuevo contacto'}>
          <div className="database-form">
            <label>Email * <input type="email" value={formContact.email} onChange={(e) => setFormContact((f) => ({ ...f, email: e.target.value }))} /></label>
            <label>Dominio <input value={formContact.domain} onChange={(e) => setFormContact((f) => ({ ...f, domain: e.target.value }))} placeholder="para vincular con organización" /></label>
            <label>Nombre <input value={formContact.name} onChange={(e) => setFormContact((f) => ({ ...f, name: e.target.value }))} /></label>
            <label>Organización <select value={formContact.organization_id} onChange={(e) => setFormContact((f) => ({ ...f, organization_id: e.target.value }))}>
              <option value="">Ninguna</option>
              {organizations.map((o) => <option key={o.id} value={o.id}>{o.name || o.domain}</option>)}
            </select></label>
            <div className="database-form-actions">
              <button type="button" className="database-btn" onClick={() => setModal(null)}>Cancelar</button>
              <button type="button" className="database-btn database-btn-primary" onClick={handleSaveContact}>Guardar</button>
            </div>
          </div>
        </Modal>
      )}

    </div>
  );
};

export default DatabaseDataSection;
